<script lang="ts">
import Notifications from "@/components/Notifications.vue"
import LocationFloatingBarOptions from "@/components/floating-bar-options/location-floating-bar-options.vue"
import GroupFloatingBarOptions from "@/components/floating-bar-options/group-floating-bar-options.vue"
import { RawLocation, Route } from "vue-router"
import { mapGetters } from "vuex"
import { AlarmAnchorTypes, AlarmCounts, Alarm } from "@/types/alarm"
import { AlarmNotificationScopes, nodeKey } from "@/types/alarm-notification"

interface LocationFooterNavProps {
    notificationsModal: boolean
    pollInterval: ReturnType<typeof setInterval> | null
    abortController: AbortController | null
}

const POLL_INTERVAL_MS = 1000 * 60

export default {
    name: "LocationFooterNav",
    components: {
        Notifications,
        LocationFloatingBarOptions,
        GroupFloatingBarOptions,
    },
    data() {
        return {
            notificationsModal: false,
            pollInterval: null,
            abortController: null,
        } as LocationFooterNavProps
    },
    watch: {
        "$route.params.locationId": "handleRouteChange",
        "$route.params.locationGroupId": "handleRouteChange",
    },
    computed: {
        ...mapGetters("sidebar", {
            isSidebarVisible: "isSidebarVisible",
        }),
        ...mapGetters("appLayoutModule", {
            isLayoutMobile: "isMobile",
            isLayoutLandscapeMobile: "isLandscapeMobile",
        }),
        alarms(): Alarm[] {
            return this.$store.getters["alarms/alarms"]
        },
        counts(): AlarmCounts | null {
            return this.$store.getters["alarms/counts"]
        },
        route(): Route {
            // @ts-ignore
            return this.$route
        },
        locationGroupId() {
            return this.route.params.locationGroupId
        },
        locationId() {
            return this.route.params.locationId
        },
        companyId() {
            return this.route.params.companyId
        },
        locationUrlPrefix() {
            const segments = []

            if (this.companyId) {
                segments.push(`companies/${this.companyId}`)
            }

            if (this.locationGroupId) {
                segments.push(`location-groups/${this.locationGroupId}`)
            }

            if (this.locationId) {
                segments.push(`locations/${this.locationId}`)
            }

            return `/${segments.join("/")}`
        },
        alarmNotificationsLink(): RawLocation {
            const focus = this.locationId
                ? nodeKey(AlarmNotificationScopes.LOCATION, Number(this.locationId))
                : nodeKey(AlarmNotificationScopes.LOCATION_GROUP, Number(this.locationGroupId))
            const query =
                this.$permissions.hasSuperAdminRole() && this.companyId
                    ? { focus, companyId: this.companyId }
                    : { focus }

            return { name: "UserAlarmNotifications", query }
        },
        notificationCount() {
            return this.counts?.total ?? 0
        },
        footerWidth() {
            if (this.isLayoutMobile) {
                return "-webkit-fill-available"
            } else {
                if (this.isSidebarVisible) {
                    return "calc(100% - 360px - 10px)"
                } else {
                    return "calc(100% - 60px)"
                }
            }
        },
    },
    methods: {
        async fetchNotifications() {
            this.abortController?.abort()

            if (!this.locationId && !this.locationGroupId) return

            this.abortController = new AbortController()
            const signal = this.abortController.signal

            if (this.locationId) {
                await this.$store.dispatch("alarms/fetchAlarmsForType", {
                    type: AlarmAnchorTypes.LOCATION,
                    id: this.locationId,
                    signal,
                })
            } else if (this.locationGroupId) {
                await this.$store.dispatch("alarms/fetchAlarmsForType", {
                    type: AlarmAnchorTypes.LOCATION_GROUP,
                    id: this.locationGroupId,
                    signal,
                })
            }
        },
        openNotificationsModal() {
            this.notificationsModal = !this.notificationsModal
        },
        handleRouteChange() {
            this.fetchNotifications()
        },
    },
    mounted() {
        this.fetchNotifications()
        this.pollInterval = setInterval(this.fetchNotifications, POLL_INTERVAL_MS)
    },
    beforeDestroy() {
        if (this.pollInterval) clearInterval(this.pollInterval)
        this.abortController?.abort()
    },
}
</script>

<template>
    <div class="location-nav-container" :style="`width: ${footerWidth}`">
        <div class="location-nav-wrapper">
            <portal to="body">
                <Notifications
                    class="notifications-modal"
                    v-if="notificationsModal"
                    v-model="notificationsModal"
                    :notifications="alarms"
                    :settings-link="alarmNotificationsLink"
                />
            </portal>

            <LocationFloatingBarOptions
                v-if="locationId"
                :notification-count="notificationCount"
                @open-notifications-modal="openNotificationsModal"
            />
            <GroupFloatingBarOptions
                v-if="locationGroupId && !locationId"
                :notification-count="notificationCount"
                @open-notifications-modal="openNotificationsModal"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.location-nav-container {
    // height: 60px !important;
}

@media (max-width: 768px) {
    .location-nav-container {
        height: 60px !important;
    }
}
</style>
